import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useAppSelector } from '../../store/hooks';
import Button from './Button';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';
  showCloseButton?: boolean;
  closeOnOverlayClick?: boolean;
  customHeader?: React.ReactNode;
  className?: string;
  contentClassName?: string;
}

// Track the number of open modals globally
let openModalCount = 0;
let savedScrollPosition = 0;
let originalBodyOverflow = '';
let originalBodyPosition = '';
let originalBodyWidth = '';
let originalHtmlOverflow = '';
let isScrollLocked = false; // Global flag to track if scroll is locked

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  showCloseButton = true,
  closeOnOverlayClick = true,
  customHeader,
  className,
  contentClassName
}) => {
  const { theme } = useAppSelector((state) => state.ui);

  // Handle escape key and scroll locking
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      
      // Only lock scroll if this is the first modal
      if (openModalCount === 0 && !isScrollLocked) {
        // Save original styles and scroll position
        originalBodyOverflow = document.body.style.overflow || '';
        originalBodyPosition = document.body.style.position || '';
        originalBodyWidth = document.body.style.width || '';
        originalHtmlOverflow = document.documentElement.style.overflow || '';
        savedScrollPosition = window.scrollY;
        
        // Prevent scrolling
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';
        document.body.style.top = `-${savedScrollPosition}px`;
        document.documentElement.style.overflow = 'hidden';
        
        isScrollLocked = true;
      }
      
      openModalCount++;

      return () => {
        document.removeEventListener('keydown', handleEscape);
        
        openModalCount--;
        
        // Only unlock scroll if this was the last modal and scroll is locked
        if (openModalCount === 0 && isScrollLocked) {
          // Restore original styles
          document.body.style.overflow = originalBodyOverflow;
          document.body.style.position = originalBodyPosition;
          document.body.style.width = originalBodyWidth;
          document.body.style.top = '';
          document.documentElement.style.overflow = originalHtmlOverflow;
          
          // Restore scroll position
          window.scrollTo(0, savedScrollPosition);
          
          isScrollLocked = false;
        }
      };
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    '2xl': 'max-w-7xl',
    '3xl': 'max-w-full sm:max-w-4xl lg:max-w-[50vw]'
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleModalScroll = (e: React.WheelEvent) => {
    // Prevent scroll from propagating to background
    e.stopPropagation();
  };

  const handleModalTouchMove = (e: React.TouchEvent) => {
    // Prevent touch scroll from propagating to background
    e.stopPropagation();
  };

  const modalContent = (
    <div 
      className={`fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-4`}
      onWheel={handleModalScroll}
      onTouchMove={handleModalTouchMove}
    >
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleOverlayClick}
      />
      
      {/* Modal */}
      <div 
        data-modal-content
        className={`relative w-full ${sizeClasses[size]} max-h-[95vh] sm:max-h-[90vh] overflow-y-auto overscroll-contain`}
        onWheel={handleModalScroll}
        onTouchMove={handleModalTouchMove}
      >
        <div
          className={`relative w-full transform overflow-hidden rounded-lg text-left shadow-xl transition-all flex flex-col ${
            theme === 'dark' 
              ? 'bg-gray-900/95 border border-gray-700/50' 
              : 'bg-white border border-gray-200/50'
          } ${className || ''}`}
        >
          {/* Header */}
          {customHeader ? (
            customHeader
          ) : title ? (
            <div className={`flex items-center justify-between border-b px-4 sm:px-6 py-3 sm:py-4 ${
              theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
            }`}>
              <h3 className={`text-base sm:text-lg font-semibold truncate pr-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{title}</h3>
              {showCloseButton && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClose}
                  className="p-2"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          ) : null}
          
          {/* Content */}
          <div className={`flex-1 overflow-y-auto ${contentClassName || 'px-4 sm:px-6 py-4'}`}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );

  // Use createPortal to render modal directly to document.body
  return createPortal(modalContent, document.body);
};

export default Modal;
